<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { publicTrialsApi } from '../../api/publicTrials';

const route = useRoute();
const router = useRouter();
const status = ref(null);
const loading = ref(true);
const errorMessage = ref(null);

const trialToken = computed(() => route.params.token);

const usagePercent = computed(
  () => status.value?.interactions?.usage_percent || 0
);
const usageColor = computed(() => {
  if (usagePercent.value >= 100) return 'bg-n-ruby-9';
  if (usagePercent.value >= 80) return 'bg-n-amber-9';
  return 'bg-n-brand';
});

const exhausted = computed(
  () => (status.value?.interactions?.remaining || 0) <= 0
);
const expiredOrExhausted = computed(
  () => exhausted.value || status.value?.trial_days_remaining <= 0
);

const fetchStatus = async () => {
  loading.value = true;
  errorMessage.value = null;
  try {
    const { data } = await publicTrialsApi.getStatus(trialToken.value);
    status.value = data;
  } catch (e) {
    errorMessage.value =
      e?.response?.status === 404
        ? 'Trial nao encontrado. Talvez voce queira comecar um novo teste?'
        : 'Erro ao carregar seu trial. Tente recarregar.';
  } finally {
    loading.value = false;
  }
};

const goToPaywall = () => {
  router.push({ name: 'trial_paywall', params: { token: trialToken.value } });
};

onMounted(fetchStatus);
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
<template>
  <div class="min-h-screen bg-n-background text-n-slate-12">
    <div class="max-w-2xl mx-auto px-6 py-16">
      <div v-if="loading" class="text-center py-16 text-n-slate-10">
        Carregando seu trial...
      </div>

      <div
        v-else-if="errorMessage"
        class="bg-n-ruby-3 border border-n-ruby-6 text-n-ruby-11 rounded-2xl p-6 text-center"
      >
        <p class="font-medium">{{ errorMessage }}</p>
        <router-link
          to="/teste-gratis"
          class="inline-block mt-4 px-4 py-2 rounded-lg bg-n-brand text-white font-semibold"
        >
          Comecar novo teste
        </router-link>
      </div>

      <div v-else>
        <div class="mb-8 text-center">
          <h1 class="text-3xl font-bold mb-2">Seu teste gratuito esta ativo</h1>
          <p class="text-n-slate-11">
            Plano
            <span class="font-semibold text-n-slate-12">{{ status.tier }}</span>
            em modo trial — {{ status.trial_days_remaining }} dias restantes
          </p>
        </div>

        <!-- Usage card -->
        <div class="bg-n-solid-1 rounded-2xl border border-n-weak p-6 mb-6">
          <div class="flex items-baseline justify-between mb-3">
            <div>
              <div
                class="text-xs uppercase tracking-wider text-n-slate-10 mb-1"
              >
                Interacoes IA
              </div>
              <div class="text-3xl font-bold">
                {{ status.interactions.consumed }}
                <span class="text-n-slate-10 text-xl">
                  / {{ status.interactions.included }}
                </span>
              </div>
            </div>
            <div class="text-right">
              <div
                class="text-xs uppercase tracking-wider text-n-slate-10 mb-1"
              >
                Usado
              </div>
              <div class="text-2xl font-bold">{{ usagePercent }}%</div>
            </div>
          </div>

          <div class="w-full h-2 bg-n-alpha-2 rounded-full overflow-hidden">
            <div
              class="h-full transition-all duration-300"
              :class="usageColor"
              :style="{ width: Math.min(usagePercent, 100) + '%' }"
            />
          </div>

          <p v-if="exhausted" class="text-sm text-n-ruby-11 mt-3">
            ⚠️ Voce atingiu o limite de interacoes do trial. Faca upgrade pra
            continuar.
          </p>
          <p v-else class="text-sm text-n-slate-10 mt-3">
            {{ status.interactions.remaining }} interacoes restantes neste ciclo
          </p>
        </div>

        <!-- CTA upgrade -->
        <div
          class="rounded-2xl p-6 text-white"
          :class="
            expiredOrExhausted
              ? 'bg-gradient-to-br from-n-ruby-9 to-n-ruby-11'
              : 'bg-gradient-to-br from-n-brand to-amber-700'
          "
        >
          <h2 class="text-xl font-bold mb-2">
            {{
              expiredOrExhausted
                ? 'Seu trial chegou ao limite'
                : 'Pronto pra escalar?'
            }}
          </h2>
          <p class="text-sm opacity-90 mb-4">
            {{
              expiredOrExhausted
                ? 'Faca upgrade agora pra continuar atendendo seus clientes sem interrupcao.'
                : 'Adquira o plano completo e desbloqueie ate 20.000 interacoes mensais, mais HDs, mais caixas.'
            }}
          </p>
          <button
            class="w-full py-3 rounded-lg font-semibold bg-white text-n-brand hover:opacity-95"
            @click="goToPaywall"
          >
            Fazer upgrade →
          </button>
        </div>

        <p class="text-xs text-n-slate-10 text-center mt-6">
          Token: <code class="font-mono">{{ trialToken }}</code>
        </p>
      </div>
    </div>
  </div>
</template>
